import TicketPickerPage from './views/ticket-picker/containers/TicketPickerPage';
import CartContainer from './views/cart/containers/CartContainer';
import ConfirmationPage from './views/confirmation/containers/ConfirmationPage';
import { fetchReservationIfNeeded, clearReservationData } from './actions/reservation';
import { setCheckoutOverlay, setConfirmation, goToListing } from './actions/ui';
import { getConfirmationData } from './actions/confirmation';
import AuthContainer from "./views/auth/containers/AuthContainer";
import EmailContainer from "./views/auth/containers/EmailContainer";
import CouponContainer from "./views/coupon/containers/CouponContainer";
import SeriesDatePicker from "./views/series/components/SeriesDatePicker";
import { getListingItem, setListingItem, removeListingItem } from 'listing/helpers/storage';
import CheckoutSteps from './views/checkout/containers/CheckoutSteps';
import KioskWaiverView from './views/questions/containers/KioskWaiverView';
import KioskDatePicker from './views/kiosk/containers/KioskDatePicker';
import KioskTimePicker from './views/kiosk/containers/KioskTimePicker';
import { getEventTimes } from './views/series/actions/event-series';
import { isValidDateObject } from '../helpers/time';
import { triggerTransition } from '@uirouter/redux';
import { reservationActive } from './selectors/reservation'
import { getMainEvent, isSeries } from './selectors/event';
import PickerWithDetailsPage from './views/picker-with-details/components/PickerWithDetails';
import DynamicListingPage from './views/dynamic-listing/components/DynamicListingPage';
import { TicketsReservedModal } from './views/picker-with-details/components/TicketsReserved';
import ContinueShoppingModal from './views/cart/components/ContinueShoppingModal';
import {fetchEvent} from './actions/event';
import { isDynamicListing } from './selectors/dynamic-listing';

let rehydratedRouterState = false;

export function getStates(store) {
    const listing = store.getState().ui.listingSlug;
    const isKioskMode = store.getState().payment.method === 13;

    const DatePickerComponent = isKioskMode ? KioskDatePicker : SeriesDatePicker;

    const hasConfirmation = () => JSON.parse(getListingItem(listing, "confirmation")) === true;

    const clearReservationWhenNavigatingFromConfirmation = () => {
        removeListingItem(listing, "confirmation");
        store.dispatch(clearReservationData({
          reset: true,
          clearChildEvent: true,
          fireCartChangeHooks: false
        }));

        if (!isSeries(store.getState())) {
            store.dispatch(fetchEvent());
        }
    }

    return [
        // {
        //     name: 'app',
        //     abstract: true,
        //     resolve: [
        //         { token: 'checkoutClient', resolveFn: () => checkoutClient },
        //         { token: 'store', resolveFn: () => store },
        //     ],
        //     resolvePolicy: {
        //         when: 'EAGER'
        //     },
        //     component: UIView
        // },
        {
            name: 'ticketPicker',
            url: '',
            component: isDynamicListing(store.getState()) ? DynamicListingPage : TicketPickerPage,
            resolve: [{
                token: 'testing',
                deps: [],
                resolveFn: () => {

                    /**
                     * Embed cannot use the url hash for getting/setting router state,
                     * and UIRouter does not offer anything persistant that works without
                     * manipulating the URL, so we have to roll our own solution.
                     * This should be the first state that embed loads when it mounts
                     * If the user was already at a different state, transition there
                     */
                    const state = store.getState();
                    if (state.ui.isEmbed && !rehydratedRouterState) {
                        rehydratedRouterState = true;
                        if (state.ui.currentRoute != 'ticketPicker') {
                            store.dispatch(triggerTransition(state.ui.currentRouteName, state.ui.currentRouteParams));
                            return Promise.resolve();
                        }
                    }

                    if (hasConfirmation()) {
                        clearReservationWhenNavigatingFromConfirmation();
                        store.dispatch(setCheckoutOverlay(false));
                    }

                    return Promise.resolve();
                }
            }]
        },
        {
          name: 'picker-with-details',
          url: '/picker-with-details',
          component: PickerWithDetailsPage
        },
        {
          name: 'tickets-reserved',
          url: '/tickets-reserved',
          component: TicketsReservedModal
        },
        {
          name: 'continue-shopping',
          url: '/continue-shopping',
          component: ContinueShoppingModal,
        },
        {
            name: 'checkout',
            url: '/checkout/:token',
            component: CheckoutSteps,
            resolve: [{
                token: 'reservationData',
                deps: ['$transition$'],
                resolveFn: (trans) => {
                    window.scrollTo(0, 0);

                    if (hasConfirmation()) {
                        clearReservationWhenNavigatingFromConfirmation();
                        store.dispatch(goToListing());
                        return;
                    }

                    return fetchReservationIfNeeded()(store.dispatch, store.getState).then(() => {
                        if (reservationActive(store.getState())) {
                            store.dispatch(setCheckoutOverlay(true));
                        } else {
                            goToListing()(store.dispatch);
                        }
                    }).catch((e) => {
                        goToListing()(store.dispatch);
                    });
                }
            }]
        },
        {
            name: 'cart',
            url: '/cart/:token',
            component: CartContainer,
            resolve: [{
                token: 'cartData',
                deps: ['$transition$'],
                resolveFn: (trans) => {
                    if (hasConfirmation()) {
                        store.dispatch(goToListing());
                        clearReservationWhenNavigatingFromConfirmation();
                        return;
                    }

                    return fetchReservationIfNeeded()(store.dispatch, store.getState).then(() => {
                        if (reservationActive(store.getState())) {
                            store.dispatch(setCheckoutOverlay(true));
                        } else {
                            goToListing()(store.dispatch);
                        }
                    }).catch(() => {
                        goToListing()(store.dispatch);
                    });
                }
            }]
        },
        {
            name: 'confirmation',
            url: '/confirmation/:confId',
            component: ConfirmationPage,
            resolve: [{
                token: 'confirmationData',
                deps: ['$transition$'],
                resolveFn: (trans) => {
                    store.dispatch(setConfirmation(true));
                    setListingItem(listing, "confirmation", JSON.stringify(true));
                    getConfirmationData(getListingItem(listing, "token"), getListingItem(listing, "secret"))(store.dispatch, store.getState).then(() => {}),
                    fetchReservationIfNeeded(false)(store.dispatch, store.getState).then(() => {})
                }
            }]
        },
        {
            name: 'pending',
            url: '/pending/:token',
            component: ConfirmationPage,
            resolve: [{
                token: 'pendingData',
                deps: ['$transition$'],
                resolveFn: trans => {
                    setListingItem(listing, "confirmation", JSON.stringify(true));
                    return fetchReservationIfNeeded()(store.dispatch, store.getState).then(() => {
                        const currentResState = store.getState().reservation;
                        if (!!currentResState.token && !currentResState?.isPendingPaymentCompletion && !currentResState?.isCompleted) {
                            removeListingItem(listing, "confirmation");
                            store.dispatch(triggerTransition('checkout', {token: currentResState.token}));
                        } else {
                            store.dispatch(setConfirmation(true));
                        }
                        store.dispatch(setCheckoutOverlay(true));
                    }).catch(err => {
                        goToListing()(store.dispatch);
                    });
                }
            }]
        },
        {
            name: 'coupon',
            url: '/promo',
            component: CouponContainer,
            resolve: [{
                token: 'testing',
                deps: ['$transition$'],
                resolveFn: () => {
                    if (hasConfirmation()) {
                        clearReservationWhenNavigatingFromConfirmation();
                    }
                    //fetchReservationIfNeeded(getListingItem(listing, "token"), getListingItem(listing, "secret"))(store.dispatch, store.getState).then(() => {});
                    return Promise.resolve();
                }
            }]
        },
        {
          name: 'auth',
          url: '/auth',
          component: AuthContainer,
          resolve: [{
            token: 'testing',
            deps: ['$transition$'],
            resolveFn: () => {
              return Promise.resolve();
            }
          }]
        },
        {
          name: 'authEmail',
          url: '/auth/email',
          params: {
            referer: null,
          },
          component: EmailContainer,
          resolve: [{
            token: 'testing',
            deps: ['$transition$'],
            resolveFn: (transition) => {
              return transition.params.referer;
            }
          }]
        },
        {
            name: 'seriesDatePicker',
            url: '/day',
            component: DatePickerComponent,
            resolve: [{
                token: 'testing',
                deps: [],
                resolveFn: () => {
                    if (hasConfirmation()) {
                        clearReservationWhenNavigatingFromConfirmation();
                    }

                    store.dispatch(setCheckoutOverlay(true));
                    return Promise.resolve().catch((e) => {console.log(e)});
                }
            }]
        },
        {
            name: 'kioskTimePicker',
            url: '/day',
            component: KioskTimePicker,
            resolve: [{
                token: 'testing',
                deps: [],
                resolveFn: () => {
                    if (hasConfirmation()) {
                        clearReservationWhenNavigatingFromConfirmation();
                    }

                    store.dispatch(setCheckoutOverlay(true));
                    return Promise.resolve().catch((e) => {console.log(e)});
                }
            }]
        },
        {
            name: 'kioskWaiverView',
            url: '/checkout/:token',
            component: KioskWaiverView,
            params: {
                question: {},
            },
            resolve: [{
                token: 'question',
                deps: ['$transition$'],
                resolveFn: (transition) => {
                    if (hasConfirmation()) {
                        clearReservationWhenNavigatingFromConfirmation();
                        store.dispatch(setCheckoutOverlay(false));
                    }
                    return transition.params().question;
                }
            }]
        },
    ]
}
